import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatusBadge } from '../components/ui/StatusBadge';
import { EmptyState } from '../components/ui/EmptyState';
import { Icon } from '../components/ui/Icon';
import {
  getPlans, getSummary, listBusinesses,
  type BusinessRow, type Lifecycle, type PlanOption, type Summary,
} from './api';
import { navigatePlatform } from './PlatformApp';
import { CreateBusinessModal } from './CreateBusinessModal';
import { usageText } from './format';

const lifecycleBadge = (l: Lifecycle) => (l === 'ACTIVE' ? <StatusBadge variant="success">Activo</StatusBadge> : l === 'SUSPENDED' ? <StatusBadge variant="warning">Suspendido</StatusBadge> : <StatusBadge variant="neutral">Archivado</StatusBadge>);
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('es-MX', { dateStyle: 'medium' });

export function PlatformHome() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [rows, setRows] = useState<BusinessRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [creating, setCreating] = useState(false);
  const limit = 25;

  // Filtros (aplicados al pulsar / cambiar).
  const [search, setSearch] = useState('');
  const [lifecycle, setLifecycle] = useState<'' | Lifecycle>('');
  const [planCode, setPlanCode] = useState('');
  const [overLimit, setOverLimit] = useState(false);
  const [applied, setApplied] = useState<{ search?: string; lifecycle?: Lifecycle; planCode?: string; overLimit?: boolean }>({});

  useEffect(() => { getPlans().then((r) => setPlans(r.plans)).catch(() => setPlans([])); }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(undefined);
    try {
      const [s, list] = await Promise.all([
        getSummary(),
        listBusinesses({ ...applied, limit, offset }),
      ]);
      setSummary(s); setRows(list.businesses); setTotal(list.pagination.total);
    } catch { setError('No fue posible cargar los negocios.'); }
    finally { setLoading(false); }
  }, [applied, offset]);

  useEffect(() => { void load(); }, [load]);

  const apply = () => {
    setOffset(0);
    setApplied({ search: search.trim() || undefined, lifecycle: lifecycle || undefined, planCode: planCode || undefined, overLimit: overLimit || undefined });
  };

  return (
    <section aria-label="Panel de plataforma">
      <PageHeader
        title="Negocios"
        description="Administra los negocios que operan en la plataforma."
        actions={<>
          <button className="button button--secondary" onClick={() => navigatePlatform('#platform/invitations')} type="button"><Icon name="users" size={16} /> Invitaciones beta</button>
          <button className="button button--primary" onClick={() => setCreating(true)} type="button"><Icon name="plus" size={16} /> Crear negocio</button>
        </>}
      />

      {summary && (
        <div className="platform-cards">
          <Card label="Total" value={summary.total} />
          <Card label="Activos" value={summary.active} variant="success" />
          <Card label="Suspendidos" value={summary.suspended} variant="warning" />
          <Card label="Archivados" value={summary.archived} variant="neutral" />
        </div>
      )}

      <fieldset className="platform-filters">
        <legend>Filtros</legend>
        <label className="field"><span>Buscar</span><input value={search} placeholder="Nombre o identificador" onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') apply(); }} /></label>
        <label className="field"><span>Estado</span>
          <select value={lifecycle} onChange={(e) => setLifecycle(e.target.value as '' | Lifecycle)}>
            <option value="">Todos</option><option value="ACTIVE">Activo</option><option value="SUSPENDED">Suspendido</option><option value="ARCHIVED">Archivado</option>
          </select>
        </label>
        <label className="field"><span>Plan</span>
          <select value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
            <option value="">Todos</option>
            {plans.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
          </select>
        </label>
        <label className="form-check form-check--inline"><input type="checkbox" checked={overLimit} onChange={(e) => setOverLimit(e.target.checked)} /> Solo sobre el límite</label>
        <button className="button button--secondary" onClick={apply} type="button"><Icon name="search" size={16} /> Aplicar</button>
      </fieldset>

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {loading ? <div className="inline-loading"><span className="spinner" />Cargando…</div> : rows.length === 0 ? (
        <EmptyState title="Sin negocios" description="No hay negocios que coincidan con los filtros." icon="stores" />
      ) : (
        <>
          <div className="data-table-wrap platform-table">
            <table className="data-table">
              <thead><tr><th>Nombre</th><th>Estado</th><th>Plan</th><th>Tiendas</th><th>Usuarios</th><th>Creado</th><th></th></tr></thead>
              <tbody>
                {rows.map((b) => (
                  <tr key={b.id} className={b.overLimit ? 'data-row--attention' : ''}>
                    <td><strong>{b.name}</strong><small>{b.slug}</small></td>
                    <td>{lifecycleBadge(b.lifecycle)}</td>
                    <td>{b.planMissing ? <StatusBadge variant="danger">Sin plan</StatusBadge> : b.planName}</td>
                    <td className={b.stores?.overLimit ? 'platform-over' : ''}>{usageText(b.stores)}</td>
                    <td className={b.users?.overLimit ? 'platform-over' : ''}>{usageText(b.users)}</td>
                    <td>{fmtDate(b.createdAt)}</td>
                    <td><button className="text-button" onClick={() => navigatePlatform(`#platform/businesses/${b.id}`)} type="button">Ver <Icon name="chevron-right" size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="platform-cards-list">
            {rows.map((b) => (
              <li key={b.id}>
                <button className="platform-biz-card" onClick={() => navigatePlatform(`#platform/businesses/${b.id}`)} type="button">
                  <div className="platform-biz-card__head"><strong>{b.name}</strong>{lifecycleBadge(b.lifecycle)}</div>
                  <span>{b.slug}</span>
                  <span>{b.planMissing ? 'Sin plan' : b.planName} · Tiendas {usageText(b.stores)} · Usuarios {usageText(b.users)}</span>
                </button>
              </li>
            ))}
          </ul>

          {total > limit && (
            <div className="platform-pager">
              <button className="text-button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} type="button">← Anteriores</button>
              <span>{offset + 1}–{Math.min(offset + rows.length, total)} de {total}</span>
              <button className="text-button" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} type="button">Siguientes →</button>
            </div>
          )}
        </>
      )}

      {creating && <CreateBusinessModal plans={plans} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); navigatePlatform(`#platform/businesses/${id}`); }} />}
    </section>
  );
}

function Card({ label, value, variant }: { label: string; value: number; variant?: 'success' | 'warning' | 'neutral' }) {
  return (
    <div className={`platform-card ${variant ? `platform-card--${variant}` : ''}`}>
      <span className="platform-card__value">{value}</span>
      <span className="platform-card__label">{label}</span>
    </div>
  );
}
